import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  CustomersHeader,
  CustomersTable,
  CustomersCards,
  CustomerModal
} from '../components/customers';
import '../styles/Customers.css';

const Customers = () => {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('customersViewMode');
    if (saved) return saved;
    return window.innerWidth <= 1024 ? 'cards' : 'table';
  });

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA',
    phone: '',
    email: '',
    contact_person: '',
    irs_ss_number: '',
    duns_number: '',
    fsvp_same_as_customer: true,
    fsvp_name: '',
    fsvp_address: '',
    fsvp_city: '',
    fsvp_state: '',
    fsvp_zip: '',
    fsvp_email: '',
    fsvp_duns_number: '',
    notes: '',
    status: 'active'
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    let filtered = customers;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCustomers(filtered);
  }, [searchTerm, statusFilter, customers]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/customers');
      setCustomers(response.data);
      setFilteredCustomers(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA',
      phone: '',
      email: '',
      contact_person: '',
      irs_ss_number: '',
      duns_number: '',
      fsvp_same_as_customer: true,
      fsvp_name: '',
      fsvp_address: '',
      fsvp_city: '',
      fsvp_state: '',
      fsvp_zip: '',
      fsvp_email: '',
      fsvp_duns_number: '',
      notes: '',
      status: 'active'
    });
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setModalMode('view');
    setShowModal(true);
  };

  const handleAddCustomer = () => {
    resetFormData();
    setSelectedCustomer(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleEditCustomer = (customer) => {
    setFormData({
      name: customer.name || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip: customer.zip || '',
      country: customer.country || 'USA',
      phone: customer.phone || '',
      email: customer.email || '',
      contact_person: customer.contact_person || '',
      irs_ss_number: customer.irs_ss_number || '',
      duns_number: customer.duns_number || '',
      fsvp_same_as_customer: customer.fsvp_same_as_customer !== null ? customer.fsvp_same_as_customer : true,
      fsvp_name: customer.fsvp_name || '',
      fsvp_address: customer.fsvp_address || '',
      fsvp_city: customer.fsvp_city || '',
      fsvp_state: customer.fsvp_state || '',
      fsvp_zip: customer.fsvp_zip || '',
      fsvp_email: customer.fsvp_email || '',
      fsvp_duns_number: customer.fsvp_duns_number || '',
      notes: customer.notes || '',
      status: customer.status || 'active'
    });
    setSelectedCustomer(customer);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleRequestDelete = (customer) => {
    setSelectedCustomer(customer);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCustomer = async () => {
    try {
      await api.delete(`/customers/${selectedCustomer.id}`);
      setShowDeleteConfirm(false);
      setShowModal(false);
      fetchCustomers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete customer');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'add') {
        const response = await api.post('/customers', formData);
        // After creation, switch to view mode to show the new customer
        const newCustomer = response.data;
        setSelectedCustomer(newCustomer);
        setModalMode('view');
        fetchCustomers();
      } else if (modalMode === 'edit') {
        await api.put(`/customers/${selectedCustomer.id}`, formData);
        // After edit, fetch updated customer and switch to view mode
        const response = await api.get(`/customers/${selectedCustomer.id}`);
        setSelectedCustomer(response.data);
        setModalMode('view');
        fetchCustomers();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save customer');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('customersViewMode', mode);
  };

  const closeModal = () => {
    setShowModal(false);
    setShowDeleteConfirm(false);
    resetFormData();
  };

  if (loading) {
    return <div className="loading">Loading customers...</div>;
  }

  return (
    <div className="customers-page">
      <CustomersHeader
        viewMode={viewMode}
        onViewModeChange={toggleViewMode}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        customerCount={filteredCustomers.length}
        isAdmin={isAdmin()}
        onAddClick={handleAddCustomer}
      />

      {error && <div className="error-message">{error}</div>}

      {viewMode === 'table' ? (
        <CustomersTable
          customers={filteredCustomers}
          isAdmin={isAdmin()}
          onView={handleViewCustomer}
          onEdit={handleEditCustomer}
          onDelete={handleRequestDelete}
        />
      ) : (
        <CustomersCards
          customers={filteredCustomers}
          isAdmin={isAdmin()}
          onView={handleViewCustomer}
          onEdit={handleEditCustomer}
          onDelete={handleRequestDelete}
        />
      )}

      {filteredCustomers.length === 0 && !loading && (
        <div className="empty-state">
          <p>No customers found</p>
          {isAdmin() && (
            <button className="btn-primary" onClick={handleAddCustomer}>
              Add First Customer
            </button>
          )}
        </div>
      )}

      <CustomerModal
        show={showModal}
        mode={modalMode}
        customer={selectedCustomer}
        formData={formData}
        isAdmin={isAdmin()}
        onClose={closeModal}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        onEdit={() => handleEditCustomer(selectedCustomer)}
        onDelete={() => setShowDeleteConfirm(true)}
      />

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete {selectedCustomer?.name}?</p>
            <p className="warning-text">
              If this customer has associated pallets, the status will be set to inactive instead.
            </p>
            <div className="delete-confirm-actions">
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDeleteCustomer}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
